import React, { useMemo, useState, useContext } from 'react';
import { useUrlDateType } from '../Hooks/useUrlDateType';
import { useUrlSorting } from '../Hooks/useUrlSorting';
import { useUrlFilters, createFieldConfig, createDateConfig } from '../Hooks/useUrlFilters';
import { useViewFilters, usePaginationReset } from '../Hooks/useViewFilters';
import { useViewQuery } from '../Hooks/useViewQuery';
import { calculateRowCount } from '../utils/viewUtils';
import { useReusableTable } from '../common/TablePanel/tablePanel';
import { MaterialReactTable } from 'material-react-table';
import FiltersComponent from '../common/FiltersPanel/FIltersPanel';
import { TimezoneContext } from '../common/TimezoneContext/TimezoneContext';
import { Box, ThemeProvider, Typography } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouterIcon from '@mui/icons-material/Router';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import DescriptionIcon from '@mui/icons-material/Description';
import RouteIcon from '@mui/icons-material/Route';
import FilterDropdown from '../common/FilterDropdown/FilterDropdown';
import './EventView.css';

const PREF_STORAGE_KEY = 'event_filter_preferences';
const DEFAULT_FIELDS = {
  vin: [],
  mac_id: [],
  trigger_type: [],
  summary: [],
  trip: [],
};

const filterConfig = {
  dateFields: [createDateConfig('start_datetime', 'end_datetime', { startParam: 'startDate', endParam: 'endDate', allowEmpty: true })],
  debounceDelay: 100,
};

const EventApp = ({ activeTheme }) => {
  const [dateType, handleDateTypeChange] = useUrlDateType();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 100 });
  const [sorting, setSorting] = useUrlSorting([{ id: 'datetime', desc: true }]);

  const mapping = { vin: 'vin', mac_id: 'mac_id', trigger_type: 'trigger_type', summary: 'summary', trip: 'trip' };

  const {
    singleValueFilters,
    debouncedSingleValueFilters,
    handleSingleFilterChange,
    handleSavePreferences,
    getOtherFilters,
    mergeField,
  } = useViewFilters({
    storageKey: PREF_STORAGE_KEY,
    defaultFields: DEFAULT_FIELDS,
    urlMapping: mapping,
    debounceDelay: 1000,
  });

  const { filters, debouncedFilters, handleDateRangeChange } = useUrlFilters(filterConfig);
  const { formatDateTime, createIsoStringWithTimezone, userTimezone, handleTimezoneChange } = useContext(TimezoneContext);

  const effectiveTimezone = userTimezone;

  const mergedFilters = useMemo(() => {
    return {
      ...debouncedFilters,
      vin: mergeField(debouncedSingleValueFilters.vin, debouncedFilters.vin),
      mac_id: mergeField(debouncedSingleValueFilters.mac_id, debouncedFilters.mac_id),
      trigger_type: mergeField(debouncedSingleValueFilters.trigger_type, debouncedFilters.trigger_type),
      summary: mergeField(debouncedSingleValueFilters.summary, debouncedFilters.summary),
      trip: mergeField(debouncedSingleValueFilters.trip, debouncedFilters.trip),
    };
  }, [debouncedFilters, debouncedSingleValueFilters, mergeField]);

  usePaginationReset(mergedFilters, dateType, setPagination);

  const eventQuery = useViewQuery({
    queryKey: 'event-data',
    mergedFilters,
    pagination,
    sorting,
    dateType,
    urlMapping: mapping,
    createIsoStringWithTimezone,
    endpoint: 'api/v2/events/query',
  });

  const data = useMemo(() => eventQuery.data?.results || [], [eventQuery.data]);
  const rowCount = useMemo(() => calculateRowCount(eventQuery.data, data, pagination), [eventQuery.data, data, pagination]);

  const columns = useMemo(() => [
    { accessorKey: 'vin', header: 'VIN' },
    { accessorKey: 'mac_id', header: 'MAC ID' },
    { accessorKey: 'datetime', header: 'Date', Cell: ({ cell }) => formatDateTime(cell.getValue(), 'YYYY-MM-DD HH:mm:ss') },
    { accessorKey: 'end_time', header: 'End Date', Cell: ({ cell }) => formatDateTime(cell.getValue(), 'YYYY-MM-DD HH:mm:ss') },
    { accessorKey: 'trigger_type', header: 'Trigger Type' },
    { accessorKey: 'summary', header: 'Summary' },
    { accessorKey: 'trip', header: 'Trip' },
    {
      accessorKey: 'additional_data',
      header: 'Additional Data',
      Cell: ({ cell }) => {
        const additionalData = cell.getValue();
        
        if (!additionalData || typeof additionalData !== 'object') {
          return <Typography variant="body2" sx={{ color: 'text.secondary' }}>N/A</Typography>;
        }
        
        const entries = Object.entries(additionalData);
        
        if (entries.length === 0) {
          return <Typography variant="body2" sx={{ color: 'text.secondary' }}>N/A</Typography>;
        }
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {entries.map(([key, value]) => (
              <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {key}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal', pl: 1 }}>
                  {typeof value === 'number' ? value.toLocaleString() : String(value)}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      },
    },
  ], [formatDateTime]);

  const dropdowns = useMemo(() => (
    <>
      <Box sx={{ mt: 2 }}>
        <FilterDropdown
          field="vin"
          label="VIN"
          value={singleValueFilters.vin}
          onChange={(value) => handleSingleFilterChange('vin', value)}
          apiBaseUrl="/api/events"
          multiple={true}
          icon={DirectionsCarIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('vin')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="mac_id"
          label="MAC ID"
          value={singleValueFilters.mac_id}
          onChange={(value) => handleSingleFilterChange('mac_id', value)}
          apiBaseUrl="/api/events"
          multiple={true}
          icon={RouterIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('mac_id')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="trigger_type"
          label="Trigger Type"
          value={singleValueFilters.trigger_type}
          onChange={(value) => handleSingleFilterChange('trigger_type', value)}
          apiBaseUrl="/api/events"
          multiple={true}
          icon={FlashOnIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('trigger_type')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="summary"
          label="Summary"
          value={singleValueFilters.summary}
          onChange={(value) => handleSingleFilterChange('summary', value)}
          apiBaseUrl="/api/events"
          multiple={true}
          icon={DescriptionIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('summary')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="trip"
          label="Trip"
          value={singleValueFilters.trip}
          onChange={(value) => handleSingleFilterChange('trip', value)}
          apiBaseUrl="/api/events"
          multiple={true}
          icon={RouteIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('trip')}
          fieldMapping={mapping}
        />
      </Box>
    </>
  ), [
    singleValueFilters,
    handleSingleFilterChange,
    debouncedFilters,
    dateType,
    createIsoStringWithTimezone,
    getOtherFilters,
    mapping
  ]);

  const { table } = useReusableTable({
    columns,
    data,
    rowCount: rowCount,
    isLoading: eventQuery.isFetching,
    pagination,
    sorting,
    onSortingChange: setSorting,
    manualPagination: true,
    onPaginationChange: setPagination,
    getRowId: row => row.id,
    enableCompareFiles: false,
    enableRowSelection: false,
    enableRowActions: false,
    enableDefaultPreview: false,
    enableDefaultDownload: false,
    enableDefaultAddToCart: false,
    enableRowExpansion: false,
    enableCSVExport: true,
    csvExportFilename: 'event-export.csv',
    maxHeight: 'calc(100vh - 250px)',
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 250px)' } },
    enableRowVirtualization: true,
    paginationDisplayMode: 'default',
    muiPaginationProps: { 
      color: 'primary', 
      rowsPerPageOptions: [100, 200, 500, 1000], 
      shape: 'rounded', 
      variant: 'outlined',
      showFirstButton: false,
      showLastButton: false
    },
  });


  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="event-main-container">
        <Box>
          <FiltersComponent
            activeTheme={activeTheme}
            dateRangePicker={{ filters: filters || {}, handleDateRangeChange }}
            timezoneSelector={{ timezone: effectiveTimezone, userTimezone, handleTimezoneChange }}
            dateTypeSelector={{ dateType, handleDateTypeChange }}
            onSaveSearch={handleSavePreferences}
            storageKey={PREF_STORAGE_KEY}
            dropdowns={dropdowns}
          />
        </Box>
        <Box className="event-content">
          <MaterialReactTable table={table} />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default EventApp;